import { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { ArrowLeft, Link } from 'lucide-react';
import { schemaLabel } from '../utils/schemaUtils';

const NODE_COLOR     = '#5a7a5a';   // moss green
const SELECTED_COLOR = '#d4821a';   // orange
const LINK_COLOR     = '#7a6a5a';   // inheritance — warm dark brown, solid arrow
const REF_LINK_COLOR = '#4a7a9a';   // reference — muted blue, dashed
const DIMMED_COLOR   = '#d8d5cc';
const TEXT_COLOR     = '#2c2620';
const MUTED_COLOR    = '#a09585';
const BG_COLOR       = '#f5f5f0';
const NODE_RADIUS    = 8;           // base radius in canvas px

function extractRefEdges(schemas) {
  const schemaByUri = {};
  schemas.forEach(s => { schemaByUri[s.schema_name] = s; });

  const edges = [];
  const seen = new Set();

  schemas.forEach(source => {
    const props = source.json_schema?.properties || {};
    Object.values(props).forEach(prop => {
      const refs = prop['x-ref-schema'];
      if (!refs) return;
      const targets = Array.isArray(refs) ? refs : [refs];
      targets.forEach(targetUri => {
        const target = schemaByUri[targetUri];
        if (!target) return;
        const key = `${source.id}→${target.id}`;
        if (seen.has(key)) return;
        seen.add(key);
        edges.push({ source: source.id, target: target.id, type: 'ref' });
      });
    });
  });
  return edges;
}

export default function SchemaNetworkGraph({ schemas, onBack, onNavigate, selectedId, highlightSearch }) {
  const containerRef = useRef(null);
  const graphRef = useRef(null);
  const centerTimeoutRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [hoveredId, setHoveredId] = useState(null);
  const [showRefs, setShowRefs] = useState(true);

  useEffect(() => {
    const measure = () => {
      if (!containerRef.current) return;
      setDimensions({
        width: containerRef.current.offsetWidth,
        height: containerRef.current.offsetHeight,
      });
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // Boost repulsion after mount
  useEffect(() => {
    const t = setTimeout(() => {
      if (!graphRef.current) return;
      const charge = graphRef.current.d3Force('charge');
      if (charge) {
        charge.strength(-220);
        graphRef.current.d3ReheatSimulation();
      }
    }, 50);
    return () => clearTimeout(t);
  }, []);

  // Center on selected node when selectedId changes
  useEffect(() => {
    if (!selectedId) return;

    const tryCenter = () => {
      if (!graphRef.current) return false;
      const nodes = graphRef.current.graphData?.()?.nodes;
      const node = nodes?.find(n => n.id === selectedId);
      if (node?.x !== undefined && node?.y !== undefined) {
        graphRef.current.centerAt(node.x, node.y, 600);
        graphRef.current.zoom(3, 600);
        return true;
      }
      return false;
    };

    if (!tryCenter()) {
      clearTimeout(centerTimeoutRef.current);
      centerTimeoutRef.current = setTimeout(tryCenter, 800);
    }

    return () => clearTimeout(centerTimeoutRef.current);
  }, [selectedId]);

  const highlightIds = useMemo(() => {
    const s = (highlightSearch || '').trim().toLowerCase();
    if (!s) return null;
    const matched = new Set(
      schemas
        .filter(sc =>
          sc.schema_name?.toLowerCase().includes(s) ||
          sc.description?.toLowerCase().includes(s)
        )
        .map(sc => sc.id)
    );
    return matched.size > 0 ? matched : new Set();
  }, [highlightSearch, schemas]);

  const graphData = useMemo(() => {
    const nodes = schemas.map(s => ({
      id: s.id,
      name: schemaLabel(s.schema_name),
      uri: s.schema_name,
      val: NODE_RADIUS * NODE_RADIUS / 4, // val drives collision sphere; keep proportional to radius²
    }));
    const nodeIds = new Set(schemas.map(s => s.id));
    const extendsLinks = schemas
      .filter(s => s.extends && nodeIds.has(s.extends))
      .map(s => ({ source: s.id, target: s.extends, type: 'extends' }));
    const refLinks = showRefs ? extractRefEdges(schemas) : [];
    return { nodes, links: [...extendsLinks, ...refLinks] };
  }, [schemas, showRefs]);

  const handleNodeClick = useCallback((node) => {
    onNavigate(node.id);
  }, [onNavigate]);

  const paintNode = useCallback((node, ctx, globalScale) => {
    const isSelected = node.id === selectedId;
    const isActive = isSelected || !highlightIds || highlightIds.has(node.id);
    const isHovered = node.id === hoveredId;
    const color = isSelected ? SELECTED_COLOR : (isActive ? NODE_COLOR : DIMMED_COLOR);
    const r = NODE_RADIUS * ((isHovered || isSelected) ? 1.25 : 1);

    // Glow for selected node
    if (isSelected) {
      ctx.save();
      ctx.shadowColor = SELECTED_COLOR;
      ctx.shadowBlur = 18;
      ctx.beginPath();
      ctx.arc(node.x, node.y, r + 4, 0, 2 * Math.PI);
      ctx.fillStyle = 'rgba(212, 130, 26, 0.18)';
      ctx.fill();
      ctx.restore();
    }

    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    if (isSelected) {
      ctx.shadowColor = SELECTED_COLOR;
      ctx.shadowBlur = 10;
    }
    ctx.fill();
    ctx.shadowBlur = 0;

    if (isHovered && !isSelected) {
      ctx.strokeStyle = TEXT_COLOR;
      ctx.lineWidth = 1.5 / globalScale;
      ctx.stroke();
    }

    const showLabel = globalScale > 0.9 || isActive || isHovered || isSelected;
    if (showLabel) {
      const fontSize = Math.max(8, 11 / globalScale);
      ctx.font = `${(isHovered || isSelected) ? '600' : '400'} ${fontSize}px Inter, sans-serif`;
      ctx.fillStyle = isActive ? TEXT_COLOR : MUTED_COLOR;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillText(node.name, node.x, node.y + r + 3 / globalScale);
    }
  }, [highlightIds, hoveredId, selectedId]);

  const paintRefLink = useCallback((link, ctx, globalScale) => {
    const start = link.source;
    const end = link.target;
    if (!start?.x || !end?.x) return;

    ctx.save();
    ctx.setLineDash([4 / globalScale, 4 / globalScale]);
    ctx.strokeStyle = REF_LINK_COLOR;
    ctx.lineWidth = 1.2 / globalScale;
    ctx.globalAlpha = 0.75;
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
    ctx.restore();
  }, []);

  const TOOLBAR_H = 44;

  return (
    <div className="schema-network" ref={containerRef}>
      <div className="schema-network__toolbar" style={{ height: TOOLBAR_H }}>
        {onBack && (
          <button className="schema-discover__back" onClick={onBack}>
            <ArrowLeft size={18} />
            <span>Schemas</span>
          </button>
        )}

        <div className="schema-network__legend">
          <span className="schema-network__legend-item">
            <span className="schema-network__legend-line schema-network__legend-line--solid" />
            inherits
          </span>
          <span className="schema-network__legend-item">
            <span className="schema-network__legend-line schema-network__legend-line--dashed" />
            references
          </span>
        </div>

        <button
          className={`schema-network__toggle${showRefs ? ' schema-network__toggle--active' : ''}`}
          onClick={() => setShowRefs(v => !v)}
          title={showRefs ? 'Hide reference edges' : 'Show reference edges'}
        >
          <Link size={14} />
          <span>Refs</span>
        </button>

        <span className="schema-network__count">{schemas.length} schemas</span>
      </div>

      <div className="schema-network__canvas">
        <ForceGraph2D
          ref={graphRef}
          graphData={graphData}
          width={dimensions.width}
          height={dimensions.height - TOOLBAR_H}
          nodeLabel={n => `${n.name}\n${n.uri || ''}`}
          nodeVal={n => n.val}
          linkColor={l => l.type === 'ref' ? 'transparent' : LINK_COLOR}
          linkWidth={l => l.type === 'ref' ? 0 : 1.5}
          linkDirectionalArrowLength={l => l.type === 'ref' ? 0 : 6}
          linkDirectionalArrowRelPos={0.88}
          linkDirectionalArrowColor={() => LINK_COLOR}
          linkCanvasObject={(link, ctx, globalScale) => {
            if (link.type === 'ref') paintRefLink(link, ctx, globalScale);
          }}
          linkCanvasObjectMode={l => l.type === 'ref' ? 'replace' : undefined}
          onNodeClick={handleNodeClick}
          onNodeHover={node => setHoveredId(node?.id || null)}
          nodeCanvasObject={paintNode}
          nodeCanvasObjectMode={() => 'replace'}
          backgroundColor={BG_COLOR}
          cooldownTicks={300}
          d3AlphaDecay={0.008}
          d3VelocityDecay={0.25}
        />
      </div>
    </div>
  );
}
